/**
 * UsageLogService.js
 * ------------------------------------------------------------
 * Centralized usage log service with Spark & Integration context tracking
 * 
 * Usage:
 * import UsageLogService from './services/UsageLogService';
 * 
 * // Initialize with Teams context
 * UsageLogService.initialize(teamsContext);
 * 
 * // Track events
 * UsageLogService.trackEvent('MessageSent', { messageLength: 150 }, 'Chat');
 * 
 * // Update Spark context
 * UsageLogService.updateSparkContext(sparkId, sparkName);
 * 
 * // Update Integration context
 * UsageLogService.updateIntegrationContext(['teams', 'onedrive']);
 * 
 * // Update Agent context (separated from integrations)
 * UsageLogService.updateAgentContext(['product_manager', 'data_analyst']);
 */

import UserProfileManager from "./UserProfileManager";
import ApiClient from "./ApiClient";

class UsageLogService {
    constructor() {
        this.apiEndpoint = '/api/UsageLog';
        this.sessionId = this.generateSessionId();
        this.userContext = null;

        // Resolved once during initialize() — stays constant for the session
        this.detectedPlatform = null;
        this.resolvedChannel   = null;

        // Spark Context (persistent across events)
        this.sparkContext = {
            sparkId: null,
            sparkName: null
        };

        // Integration Context (separate from agents)
        this.integrationContext = {
            integrations: []
        };

        // Agent Context (separated for independent tracking)
        this.agentContext = {
            agents: []
        };

        // Track session start time for duration calculations
        this.sessionStartTime = Date.now();
    }

    /**
     * Generate unique session ID
     */
    generateSessionId() {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Initialize with Teams context
     */
    async initialize(teamsContext) {
        this.userContext = {
            userId: teamsContext.user?.id || 'unknown',
            userPrincipalName: teamsContext.user?.userPrincipalName || 'unknown'
        };

        // Resolve platform & channel once for the entire session
        this.detectedPlatform = this.detectPlatform(teamsContext);
        this.resolvedChannel  = this._channelForPlatform(this.detectedPlatform);

        console.log(`[UsageLog] Initialized — Session: ${this.sessionId} | Platform: ${this.detectedPlatform} | Channel: ${this.resolvedChannel}`);

        // Track app initialization
        await this.trackEvent('AppInitialized', {
            teamsTheme: teamsContext.theme || 'default',
            locale: teamsContext.locale || 'en-us',
            platform: this.detectedPlatform
        }, 'Session');
    }

     /**
      * Detect platform using Teams SDK hostClientType (most reliable) with
      * User-Agent as fallback.
      * Returns one of: 'Android' | 'iOS' | 'TeamsDesktop' | 'TeamsWeb'
      */
     detectPlatform(teamsContext) {
         // Teams SDK v2 exposes app.host.clientType
         const clientType = teamsContext?.app?.host?.clientType
             || teamsContext?.hostClientType   // Teams SDK v1 fallback
             || '';

         const ct = clientType.toLowerCase();
         if (ct === 'android') return 'Android';
         if (ct === 'ios')     return 'iOS';
         if (ct === 'desktop' || ct === 'teamsdesktop') return 'TeamsDesktop';
         if (ct === 'web')     return 'TeamsWeb';

         // User-Agent fallback when hostClientType is absent
         const ua = navigator.userAgent || navigator.vendor || window.opera || '';
         if (/android/i.test(ua))                         return 'Android';
         if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'iOS';

         return 'TeamsWeb';
     }

     /**
      * Map a detected platform to the canonical channel value stored in CosmosDB.
      * Mobile devices → "Spark Teams Mobile"
      * Desktop/Web    → "SparkTeams"
      */
     _channelForPlatform(platform) {
         return (platform === 'Android' || platform === 'iOS')
             ? 'Spark Teams Mobile'
             : 'SparkTeams';
     }

    /**
     * Update active Spark context
     */
    updateSparkContext(sparkId, sparkName) {
        const previousSparkId = this.sparkContext.sparkId;
        
        this.sparkContext = {
            sparkId: sparkId,
            sparkName: sparkName
        };

        console.log(`[UsageLog] Spark context: ${sparkName} (${sparkId})`);

        // Track spark activation/switch
        if (previousSparkId !== sparkId) {
            this.trackEvent('SparkActivated', {
                sparkId: sparkId,
                sparkName: sparkName,
                previousSparkId: previousSparkId
            }, 'Spark');
        }
    }

    /**
     * Update active Integration context (integrations only)
     * @param {Array<string>} integrations - Array of active integration names (e.g., ['teams', 'onedrive', 'ado'])
     */
    updateIntegrationContext(integrations = []) {
        this.integrationContext = {
            integrations: integrations
        };

        console.log(`[UsageLog] Integrations: [${integrations.join(', ')}]`);
    }

    /**
     * Update active Agent context (separated from integrations)
     * @param {Array<string>} agents - Array of active agent names (e.g., ['product_manager', 'data_analyst'])
     */
    updateAgentContext(agents = []) {
        this.agentContext = {
            agents: agents
        };

        console.log(`[UsageLog] Agents: [${agents.join(', ')}]`);
    }

    /**
     * Clear Spark context (when switching or resetting)
     */
    clearSparkContext() {
        this.sparkContext = { sparkId: null, sparkName: null };
    }

    /**
     * Clear Integration context
     */
    clearIntegrationContext() {
        this.integrationContext = { integrations: [] };
    }

    /**
     * Clear Agent context
     */
    clearAgentContext() {
        this.agentContext = { agents: [] };
    }

    /**
     * Track user event/action
     * @param {string} eventName - Name of the event (e.g., 'SparkActivated', 'MessageSent')
     * @param {object} properties - Additional metadata
     * @param {string} category - Event category (e.g., 'Chat', 'Integration', 'Navigation')
     * @param {string|null} channel - Override channel; defaults to session-resolved channel
     *   Mobile devices resolve to "Spark Teams Mobile", desktop/web to "SparkTeams".
     */
    async trackEvent(eventName, properties = {}, category = 'General', channel = null) {
        // Validate user context
        if (!this.userContext || !this.userContext.userId) {
            console.warn('[UsageLog] User context not initialized');
            return;
        }

        // Use session-resolved channel (mobile-aware) unless an explicit override is given
        const resolvedChannel = channel || this.resolvedChannel || 'SparkTeams';

        // Get extended profile from UserProfileManager (instant - from localStorage)
        const profile = UserProfileManager.getProfile();

        const usageLogEvent = {
            sessionId: this.sessionId,
            eventName,
            category,
            channel: resolvedChannel,
            properties: JSON.stringify({ ...properties, platform: this.detectedPlatform }),

            // Spark Context (separate fields)
            sparkId: this.sparkContext.sparkId,
            sparkName: this.sparkContext.sparkName,

            // Integration Context (separate field)
            integrations: this.integrationContext.integrations.length > 0 
                ? this.integrationContext.integrations.join(',') 
                : null,

            // Agent Context (separate field - now independent)
            agents: this.agentContext.agents.length > 0 
                ? this.agentContext.agents.join(',') 
                : null,

            // Basic User Info
            userId: this.userContext?.userId,
            userPrincipalName: this.userContext?.userPrincipalName,            

            // Extended User Info (from Graph API - may be null initially)
            userName: profile?.displayName || null,
            jobTitle: profile?.jobTitle || null,
            department: profile?.department || null,
            officeLocation: profile?.officeLocation || null,
            city: profile?.city || null,
            country: profile?.country || null,
            companyName: profile?.companyName || null,
            managerEmail: profile?.manager?.mail || null,
            managerName: profile?.manager?.displayName || null
        };

        try {
            await ApiClient.post(this.apiEndpoint, usageLogEvent);
        } catch (error) {
            // Silent failure - don't disrupt user experience
            console.warn('[UsageLog] Tracking error:', error.message || error);
        }
    }

    /**
     * Track API call with timing
     */
    async trackApiCall(apiName, method, statusCode, duration, metadata = {}) {
        await this.trackEvent('ApiCall', {
            apiName,
            method,
            statusCode,
            duration,
            success: statusCode >= 200 && statusCode < 300,
            ...metadata,
        }, 'API');
    }

    /**
     * Track error
     */
    async trackError(error, context = {}) {
        await this.trackEvent('Error', {
            errorMessage: error.message || 'Unknown error',
            errorStack: error.stack || '',
            errorType: error.name || 'Error',
            context: JSON.stringify(context),
        }, 'Error');
    }

    /**
     * Track page/view navigation
     */
    async trackPageView(pageName, metadata = {}) {
        await this.trackEvent('PageView', { 
            pageName,
            timestamp: new Date().toISOString(),
            ...metadata 
        }, 'Navigation');
    }

    /**
     * Track user interaction (button clicks, menu selections, etc.)
     */
    async trackInteraction(interactionType, target, metadata = {}) {
        await this.trackEvent('UserInteraction', {
            interactionType,
            target,
            ...metadata
        }, 'Interaction');
    }

    /**
     * Track chat message sent
     */
    async trackMessageSent(messageLength, sparkId, sparkName, integrations = []) {
        await this.trackEvent('MessageSent', {
            messageLength,
            hasAttachments: false,
            responseTime: null
        }, 'Chat');
    }

    /**
     * Track chat message received
     */
    async trackMessageReceived(responseTime, tokenCount, sparkId) {
        await this.trackEvent('MessageReceived', {
            responseTime,
            tokenCount,
            success: true
        }, 'Chat');
    }

    /**
     * Track feature usage
     */
    async trackFeatureUsage(featureName, action, metadata = {}) {
        await this.trackEvent('FeatureUsage', {
            featureName,
            action, // 'opened', 'closed', 'configured', 'used'
            ...metadata
        }, 'Feature', 'SparkTeams');
    }

    /**
     * Track search queries
     */
    async trackSearch(searchQuery, resultCount, metadata = {}) {
        await this.trackEvent('Search', {
            searchQuery: searchQuery.substring(0, 100), // Limit PII
            resultCount,
            ...metadata
        }, 'Search', 'SparkTeams');
    }

    /**
     * Track integration activation/deactivation
     */
    async trackIntegrationToggle(integrationType, enabled, metadata = {}) {
        await this.trackEvent('IntegrationToggle', {
            integrationType,
            enabled,
            ...metadata
        }, 'Integration', 'SparkTeams');
    }

    /**
     * Track agent activation
     */
    async trackAgentActivated(agentName, agentId, metadata = {}) {
        await this.trackEvent('AgentActivated', {
            agentName,
            agentId,
            ...metadata
        }, 'Agent', 'SparkTeams');
    }

    /**
     * Track session end (call on app unload)
     */
    async trackSessionEnd() {
        const sessionDuration = Date.now() - this.sessionStartTime;
        
        await this.trackEvent('SessionEnd', {
            sessionDuration,
            sessionDurationMinutes: Math.round(sessionDuration / 60000)
        }, 'Session', 'SparkTeams');
    }

    /**
     * Enable automatic page tracking (call once in app initialization)
     */
    enableAutoTracking() {
        // Track page visibility changes
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.trackEvent('AppHidden', {}, 'Session');
            } else {
                this.trackEvent('AppVisible', {}, 'Session');
            }
        });

        // Track session end on page unload
        window.addEventListener('beforeunload', () => {
            this.trackSessionEnd();
        });

        console.log('[UsageLog] Auto-tracking enabled');
    }
}

// Export singleton instance
const usageLogService = new UsageLogService();
export default usageLogService;